import { useState, useEffect, useReducer, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import * as ACT from 'components/proposals/Proposal/actions'
import { EmailProposalModal } from './EmailProposalModal'
import { toProposalContactsSelectValue, toUsers } from 'components/proposals/Proposal/serializers'
import { makeGetRequest } from 'utils/api'
import update from 'immutability-helper'

const initialState = {
  toContacts: [],
  ccContacts: [],
  bccContacts: '',
  files: []
}

const reducer = (state, action) => {
  const { value, property, file } = action.payload
  let index
  switch (action.type) {
    case ACT.CHANGE_EMAIL_CONTACTS:
      return { ...state, [property]: value }
    case ACT.RESET_EMAIL_CONTACTS:
      return initialState
    case ACT.ADD_FILE:
      index = state.files.findIndex(f => f.name === file.name)
      if (~index) {
        return state
      } else {
        return update(state, {
          files: { $unshift: [file] }
        })
      }
    case ACT.REMOVE_FILE:
      index = state.files.findIndex(f => f.name === file.name)
      return update(state, {
        files: {
          $splice: [[index, 1]]
        }
      })
    default:
      return state
  }
}

const getEmailBody = async (id, cb) => {
  const url = `/v1/proposals/email/template/${id}`
  const emailTemplate = await makeGetRequest(url)

  cb && cb(emailTemplate)
}

const getProposalMainContacts = async (id, cb) => {
  const url = `/v1/proposals/email/main-contacts/${id}`
  const contacts = await makeGetRequest(url)
  cb && cb(toProposalContactsSelectValue(contacts), 'toContacts')
}

const getProposalOtherContacts = async (id, cb) => {
  const url = `/v1/proposals/email/other-contacts/${id}`
  const contacts = await makeGetRequest(url)
  cb && cb(toProposalContactsSelectValue(contacts))
}

const getTenantUsers = async cb => {
  const { content } = await makeGetRequest('/v1/users')
  cb && cb(toUsers(content))
}

const EmailProposalModalContainer = ({
  proposal,
  toggleEmailProposalModal,
  showEmailProposalModal
}) => {
  const [subject, setSubject] = useState('')
  const [emailBody, setEmailBody] = useState(proposal.emailBody)
  const [mainContacts, setMainContacts] = useState([])
  const [otherContacts, setOtherContacts] = useState([])
  const [tenantUsers, setTenantUsers] = useState([])
  const [allCCContacts, setAllCCContacts] = useState([])
  const [emailInfo, dispatchState] = useReducer(reducer, initialState)

  const setToContacts = useCallback(
    (value, property) => {
      setMainContacts(value)
      dispatchState({
        type: ACT.CHANGE_EMAIL_CONTACTS,
        payload: {
          value,
          property
        }
      })
    },
    [setMainContacts]
  )

  const changeContacts = (value, property) => {
    dispatchState({
      type: ACT.CHANGE_EMAIL_CONTACTS,
      payload: {
        value,
        property
      }
    })
  }

  const dispatch = useDispatch()

  useEffect(() => {
    setSubject(proposal.name)
  }, [proposal])

  useEffect(() => {
    const contacts = mainContacts.concat(otherContacts, tenantUsers)
    setAllCCContacts(contacts)
  }, [proposal, mainContacts, otherContacts, tenantUsers])

  useEffect(() => {
    if (proposal.id && showEmailProposalModal) {
      getProposalMainContacts(proposal.id, setToContacts)
      getProposalOtherContacts(proposal.id, setOtherContacts)
      getTenantUsers(setTenantUsers)
    }
  }, [proposal.id, setToContacts, showEmailProposalModal])

  useEffect(() => {
    proposal.id && showEmailProposalModal && getEmailBody(proposal.id, setEmailBody)
  }, [proposal.id, showEmailProposalModal])

  useEffect(() => {
    if (!showEmailProposalModal) {
      dispatchState({
        type: ACT.RESET_EMAIL_CONTACTS,
        payload: {}
      })
      setSubject(proposal.name)
    }
  }, [proposal, showEmailProposalModal])

  const { toContacts, ccContacts, bccContacts, files } = emailInfo

  const sendEmail = (template, destroyEditor, splitMode) => {
    const { id } = proposal
    dispatch({
      type: ACT.EMAIL_PROPOSAL_TO_CLIENT_REQUESTED,
      payload: {
        id,
        toContacts,
        ccContacts,
        bccContacts,
        subject,
        emailBody: template,
        files,
        splitMode
      }
    })
    destroyEditor?.()
    toggleEmailProposalModal()
  }

  const uploadFiles = e => {
    const currentFiles = e.target.files

    for (const file of currentFiles) {
      const fr = new FileReader()
      fr.onload = () => {
        dispatchState({
          type: ACT.ADD_FILE,
          payload: {
            file
          }
        })
      }
      fr.readAsDataURL(file)
    }

    e.target.value = ''
  }

  const removeFile = file => {
    dispatchState({
      type: ACT.REMOVE_FILE,
      payload: {
        file
      }
    })
  }

  return (
    <EmailProposalModal
      proposal={proposal}
      mainContacts={mainContacts}
      allCCContacts={allCCContacts}
      toContacts={toContacts}
      ccContacts={ccContacts}
      bccContacts={bccContacts}
      subject={subject}
      setSubject={setSubject}
      emailBody={emailBody}
      toggleEmailProposalModal={toggleEmailProposalModal}
      showEmailProposalModal={showEmailProposalModal}
      changeContacts={changeContacts}
      setEmailBody={setEmailBody}
      sendEmail={sendEmail}
      uploadFiles={uploadFiles}
      files={files}
      removeFile={removeFile}
    />
  )
}

export default EmailProposalModalContainer
